import { lazy, Suspense, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bike, Boxes, CalendarClock, MapPin, Package, Plus } from 'lucide-react';
import {
  CITY,
  orderStatusLabel,
  OrderStatus,
  OrderType,
  PARCEL_APPROVAL_LABELS,
  PARCEL_SIZE_HINTS,
  PARCEL_SIZE_LABELS,
  ParcelApproval,
  ParcelSize,
  type LatLng,
} from '@kick/types';
import { createMerchantParcelSchema } from '@kick/validation';
import { getErrorMessage } from '@/lib/api';
import { textoUbicacion, useUbicacionAutomatica } from '@/lib/geocodificar';
import { cn, desdeHace, formatCOP, formatDistance } from '@/lib/cn';
import {
  useCotizarEnvio,
  useEnviosDelComercio,
  useLotesDelComercio,
  useMerchantId,
  usePedirEnvio,
  type EnvioDelComercio,
} from '@/lib/queries';
import { Boton, Campo, Cargando, Entrada, ErrorCarga, Modal, Vacio } from '@/components/ui';
import { useAvisos } from '@/components/avisos';
import { AvisoPlataformaCerrada, usePlataformaCerrada } from '@/components/AvisoPlataformaCerrada';

/** El mapa arrastra MapLibre: solo se descarga al abrir el formulario. */
const SelectorPunto = lazy(() =>
  import('@/components/SelectorPunto').then((m) => ({ default: m.SelectorPunto })),
);

/**
 * El comercio pide un domiciliario para lo suyo.
 *
 * Da igual si lo que sale es una venta hecha por WhatsApp o un paquete para un
 * proveedor: recoger aquí y llevar allá es el mismo encargo. Por eso hay una
 * sola pantalla y no dos.
 *
 * No se pregunta dónde recoger —es su tienda, y su dirección y su marca en el
 * mapa ya están registradas— ni cómo se paga: va a su cuenta y se le descuenta
 * en el cierre del período, junto con las comisiones.
 */
export function Envios() {
  const merchantId = useMerchantId();
  const envios = useEnviosDelComercio(merchantId);
  const [pidiendo, setPidiendo] = useState(false);
  const cerrada = usePlataformaCerrada();

  if (envios.isLoading) return <Cargando texto="Cargando tus envíos…" />;
  if (envios.isError) {
    return (
      <ErrorCarga mensaje={getErrorMessage(envios.error)} onReintentar={() => void envios.refetch()} />
    );
  }

  const lista = envios.data ?? [];

  return (
    <div className="mx-auto max-w-4xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl text-kick-dark">Envíos</h1>
          <p className="mt-1 text-sm text-kick-gray">
            Pide un domiciliario para lo que necesites sacar del local. Le pagas el domicilio al
            domiciliario cuando recoge, como en cualquier pedido.
          </p>
        </div>
        {!cerrada && (
          <div className="flex flex-wrap gap-2">
            <Link
              to="/envios/masivos"
              className="inline-flex items-center gap-2 rounded-kick border border-kick-gray/20 bg-white px-4 py-2 text-sm font-semibold text-kick-ink transition hover:border-kick-dark"
            >
              <Boxes className="h-4 w-4" />
              Envíos masivos
            </Link>
            <Boton icono={<Plus className="h-4 w-4" />} onClick={() => setPidiendo(true)}>
              Pedir domiciliario
            </Boton>
          </div>
        )}
      </div>

      <AvisoPlataformaCerrada />

      <Lotes merchantId={merchantId} />

      {lista.length === 0 ? (
        <div className="mt-6">
          <Vacio
            icono={<Bike className="h-6 w-6" />}
            titulo="Todavía no has pedido ninguno"
            descripcion="Cuando necesites sacar algo del local, pide un domiciliario aquí."
          />
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {lista.map((e) => (
            <Fila key={e.orderId} envio={e} />
          ))}
        </div>
      )}

      <Formulario
        abierto={pidiendo && !cerrada}
        merchantId={merchantId}
        onCerrar={() => setPidiendo(false)}
      />
    </div>
  );
}

function Fila({ envio: e }: { envio: EnvioDelComercio }) {
  const programado = !!e.scheduledFor;
  const esperando = e.approval === ParcelApproval.PENDING;

  return (
    <article className="rounded-kick bg-white p-4 shadow-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono font-bold text-kick-dark">{e.code}</span>
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-xs font-semibold',
                e.status === OrderStatus.DELIVERED
                  ? 'bg-emerald-100 text-emerald-900'
                  : e.status === OrderStatus.CANCELLED
                    ? 'bg-red-100 text-red-900'
                    : 'bg-sky-100 text-sky-900',
              )}
            >
              {orderStatusLabel(e.status, OrderType.PARCEL)}
            </span>
            {esperando && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900">
                {PARCEL_APPROVAL_LABELS[ParcelApproval.PENDING]}
              </span>
            )}
          </div>

          <p className="mt-1 text-sm text-kick-ink">{e.description}</p>
          <p className="text-sm text-kick-gray">
            {e.recipientName} · {e.deliveryAddress.addressLine} · {formatDistance(e.distanceMeters)}
          </p>

          {programado && (
            <p className="mt-1 flex items-center gap-1.5 text-sm text-kick-gray">
              <CalendarClock className="h-3.5 w-3.5" />
              {new Date(e.scheduledFor!).toLocaleString('es-CO', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                hour: '2-digit',
                minute: '2-digit',
                timeZone: 'America/Bogota',
              })}
            </p>
          )}

          {!!e.decisionNote && (
            <p className="mt-1 text-sm italic text-kick-gray">«{e.decisionNote}»</p>
          )}
        </div>

        <div className="text-right">
          <p className="font-bold text-kick-dark">{formatCOP(e.total)}</p>
          <p className="text-xs text-kick-gray">{desdeHace(e.createdAt)}</p>
        </div>
      </div>
    </article>
  );
}

function Formulario({
  abierto,
  merchantId,
  onCerrar,
}: {
  abierto: boolean;
  merchantId: string;
  onCerrar: () => void;
}) {
  const pedir = usePedirEnvio(merchantId);
  const avisos = useAvisos();

  const [destino, setDestino] = useState<LatLng>({ ...CITY.center });
  const [direccion, setDireccion] = useState('');
  const [detalles, setDetalles] = useState('');
  const [size, setSize] = useState<ParcelSize>(ParcelSize.MEDIUM);
  const [descripcion, setDescripcion] = useState('');
  const [recibe, setRecibe] = useState('');
  const [telefono, setTelefono] = useState('');
  const [dondeRecoger, setDondeRecoger] = useState('');
  const [notas, setNotas] = useState('');
  const [errores, setErrores] = useState<Record<string, string>>({});

  // La dirección escrita se busca sola en el mapa; el pin se puede mover después.
  const ubicacion = useUbicacionAutomatica(direccion, setDestino, destino);
  const estadoMapa = textoUbicacion(ubicacion);

  // Se cotiza en cuanto hay dirección escrita: el precio decide si sigue.
  const cotizacion = useCotizarEnvio(merchantId, direccion.trim() ? destino : null, size);

  const enviar = (): void => {
    const datos = {
      dropoff: {
        addressLine: direccion.trim(),
        ...(detalles.trim() ? { details: detalles.trim() } : {}),
        location: destino,
      },
      size,
      description: descripcion.trim(),
      recipientName: recibe.trim(),
      recipientPhone: telefono.trim(),
      ...(dondeRecoger.trim() ? { pickupDetails: dondeRecoger.trim() } : {}),
      ...(notas.trim() ? { customerNotes: notas.trim() } : {}),
    };

    // Mismo esquema que el servidor: señala el campo en vez de un 400 genérico.
    const revision = createMerchantParcelSchema.safeParse(datos);
    if (!revision.success) {
      const encontrados: Record<string, string> = {};
      for (const problema of revision.error.issues) {
        const campo = problema.path.join('.');
        encontrados[campo] ??= problema.message;
      }
      setErrores(encontrados);
      return;
    }

    setErrores({});
    pedir.mutate(datos, {
      onSuccess: (envio) => {
        avisos.exito(`${envio.code} pedido. Buscando domiciliario…`);
        setDireccion('');
        setDetalles('');
        setDescripcion('');
        setRecibe('');
        setTelefono('');
        setDondeRecoger('');
        setNotas('');
        onCerrar();
      },
      onError: (e) => avisos.error(getErrorMessage(e)),
    });
  };

  return (
    <Modal abierto={abierto} onCerrar={onCerrar} titulo="Pedir domiciliario" ancho="lg">
      <div className="space-y-5 p-5">
        <div className="flex items-start gap-3 rounded-kick bg-kick-light p-3">
          <Package className="mt-0.5 h-4 w-4 shrink-0 text-kick-gray" />
          <p className="text-sm text-kick-gray">
            Recogemos en tu local. El domicilio se lo pagas al domiciliario en efectivo cuando
            recoge: no se carga a tu cuenta.
          </p>
        </div>

        <Campo etiqueta="Dirección de entrega" requerido error={errores['dropoff.addressLine']}>
          <Entrada
            value={direccion}
            onChange={(e) => setDireccion(e.target.value)}
            maxLength={250}
            placeholder="Ej: Calle 10 #43-25, El Poblado"
            error={!!errores['dropoff.addressLine']}
          />
        </Campo>

        <Campo etiqueta="Detalles de la entrega" ayuda="Torre, apartamento, punto de referencia.">
          <Entrada
            value={detalles}
            onChange={(e) => setDetalles(e.target.value)}
            maxLength={150}
            placeholder="Ej: Torre B, apto 502"
          />
        </Campo>

        <div>
          <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-kick-ink">
            <MapPin className="h-4 w-4 text-kick-red" />
            Marca el punto exacto
          </p>
          <Suspense fallback={<div className="h-64 animate-pulse rounded-kick bg-kick-gray/10" />}>
            <SelectorPunto valor={destino} onCambio={setDestino} />
          </Suspense>
          <p
            className={cn(
              'mt-2 text-xs',
              estadoMapa.tono === 'ok'
                ? 'text-emerald-700'
                : estadoMapa.tono === 'aviso'
                  ? 'text-amber-700'
                  : 'text-kick-gray',
            )}
          >
            {estadoMapa.texto} De aquí sale la distancia y el precio.
          </p>
        </div>

        <div>
          <p className="font-bold text-kick-dark">¿Qué mandas?</p>
          <div className="mt-3 grid gap-2">
            {Object.values(ParcelSize).map((opcion) => (
              <button
                key={opcion}
                type="button"
                onClick={() => setSize(opcion)}
                aria-pressed={size === opcion}
                className={cn(
                  'rounded-kick border p-3 text-left transition',
                  size === opcion
                    ? 'border-kick-red bg-kick-red/5'
                    : 'border-kick-gray/25 hover:border-kick-gray/50',
                )}
              >
                <p className="font-semibold text-kick-ink">{PARCEL_SIZE_LABELS[opcion]}</p>
                <p className="mt-0.5 text-xs text-kick-gray">{PARCEL_SIZE_HINTS[opcion]}</p>
              </button>
            ))}
          </div>
        </div>

        <Campo etiqueta="Descríbelo" requerido error={errores.description}>
          <Entrada
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            maxLength={300}
            placeholder="Ej: pedido de la señora Marta, 2 bandejas"
            error={!!errores.description}
          />
        </Campo>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo etiqueta="Quién recibe" requerido error={errores.recipientName}>
            <Entrada
              value={recibe}
              onChange={(e) => setRecibe(e.target.value)}
              maxLength={140}
              placeholder="Nombre completo"
              error={!!errores.recipientName}
            />
          </Campo>
          <Campo etiqueta="Su teléfono" requerido error={errores.recipientPhone}>
            <Entrada
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              inputMode="tel"
              placeholder="3001234567"
              error={!!errores.recipientPhone}
            />
          </Campo>
        </div>

        <Campo
          etiqueta="Dónde recogerlo dentro del local"
          ayuda="Por si no es en el mostrador."
          error={errores.pickupDetails}
        >
          <Entrada
            value={dondeRecoger}
            onChange={(e) => setDondeRecoger(e.target.value)}
            maxLength={150}
            placeholder="Ej: en la bodega, pregunta por Luis"
          />
        </Campo>

        <Campo etiqueta="Algo más que deba saber el domiciliario" error={errores.customerNotes}>
          <Entrada
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            maxLength={300}
            placeholder="Ej: llamar antes de subir"
          />
        </Campo>

        <Precio cotizacion={cotizacion.data} cargando={cotizacion.isFetching} />

        <div className="flex gap-3">
          <Boton variante="secundario" className="flex-1" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton
            className="flex-1"
            cargando={pedir.isPending}
            disabled={cotizacion.data ? !cotizacion.data.covered : true}
            onClick={enviar}
          >
            {/* Sin cobertura no hay precio que enseñar: «Pedir por $0» miente. */}
            {cotizacion.data?.covered
              ? `Pedir por ${formatCOP(cotizacion.data.total)}`
              : 'Pedir domiciliario'}
          </Boton>
        </div>
      </div>
    </Modal>
  );
}

function Precio({
  cotizacion,
  cargando,
}: {
  cotizacion: { total: number; deliveryFee: number; rainFee: number; sizeFee: number; covered: boolean; distanceMeters: number; estimatedMinutes: number } | undefined;
  cargando: boolean;
}) {
  if (!cotizacion) {
    return (
      <p className="text-center text-sm text-kick-gray">
        {cargando ? 'Calculando el precio…' : 'Escribe la dirección y marca el punto para ver el precio.'}
      </p>
    );
  }

  if (!cotizacion.covered) {
    return (
      <div className="rounded-kick border border-amber-200 bg-amber-50 p-3">
        <p className="text-sm font-semibold text-amber-900">Fuera de cobertura</p>
        <p className="mt-0.5 text-sm text-amber-800">
          Esa dirección está fuera de las zonas donde operamos. Mueve el punto o revisa la
          dirección.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-kick bg-kick-light p-4">
      <dl className="space-y-1.5 text-sm">
        <div className="flex justify-between text-kick-gray">
          <dt>Recorrido · {(cotizacion.distanceMeters / 1000).toFixed(1)} km</dt>
          <dd>{formatCOP(cotizacion.deliveryFee)}</dd>
        </div>
        {cotizacion.sizeFee > 0 && (
          <div className="flex justify-between text-kick-gray">
            <dt>Recargo por tamaño</dt>
            <dd>{formatCOP(cotizacion.sizeFee)}</dd>
          </div>
        )}
        {cotizacion.rainFee > 0 && (
          <div className="flex justify-between text-kick-gray">
            <dt>Tarifa lluvia</dt>
            <dd>{formatCOP(cotizacion.rainFee)}</dd>
          </div>
        )}
        <div className="flex justify-between border-t border-kick-gray/20 pt-1.5 text-base font-bold text-kick-dark">
          <dt>Pagas al recoger</dt>
          <dd>{formatCOP(cotizacion.total)}</dd>
        </div>
      </dl>
      <p className="mt-2 text-xs text-kick-gray">
        Llega en unos {cotizacion.estimatedMinutes} minutos desde que lo recogen.
      </p>
    </div>
  );
}

/**
 * Los lotes de envíos masivos del comercio, con cuántos van.
 *
 * Un lote no es un pedido: son diez o veinte, y lo que el comercio quiere
 * saber es cuántos ya salieron y cuántos siguen esperando domiciliario.
 */
function Lotes({ merchantId }: { merchantId: string }) {
  const lotes = useLotesDelComercio(merchantId);
  const lista = (lotes.data ?? []).filter((l) => l.sinAsignar + l.enCurso > 0 || l.total > 0);
  if (lista.length === 0) return null;

  return (
    <section className="mt-6">
      <h2 className="text-lg text-kick-dark">Envíos masivos</h2>
      <div className="mt-2 space-y-2">
        {lista.slice(0, 10).map((l) => (
          <article key={l.id} className="card-kick flex flex-wrap items-center gap-3 p-3.5">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-kick-light text-kick-dark">
              <Boxes className="h-4 w-4" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-semibold text-kick-ink">
                Lote de {l.total} envío{l.total === 1 ? '' : 's'} · {formatCOP(l.monto)}
              </span>
              <span className="block text-sm text-kick-gray">
                {desdeHace(l.createdAt)}
                {l.notes ? ` · ${l.notes}` : ''}
              </span>
            </span>
            <span className="flex flex-wrap gap-1.5 text-xs">
              {l.sinAsignar > 0 && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-800">
                  {l.sinAsignar} esperando domiciliario
                </span>
              )}
              {l.enCurso > 0 && (
                <span className="rounded-full bg-blue-100 px-2 py-0.5 font-semibold text-blue-800">
                  {l.enCurso} en curso
                </span>
              )}
              {l.entregados > 0 && (
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-800">
                  {l.entregados} entregado{l.entregados === 1 ? '' : 's'}
                </span>
              )}
              {l.cancelados > 0 && (
                <span className="rounded-full bg-red-100 px-2 py-0.5 font-semibold text-red-800">
                  {l.cancelados} cancelado{l.cancelados === 1 ? '' : 's'}
                </span>
              )}
            </span>
          </article>
        ))}
      </div>
    </section>
  );
}
